import { Game, RoundState } from './game/Game';
import {
	INITIAL_BALANCE,
	STORAGE_KEY_BALANCE,
	STORAGE_KEY_AUDIO_PREFS,
	BET_PHASE_MS,
	MOBILE_BREAKPOINT,
} from './game/constants';
import { RocketAnimation } from './components/RocketAnimation';
import { StarsBackground } from './components/StarsBackground';
import { FloatingElements } from './components/FloatingElements';
import { AcidSmoke } from './components/AcidSmoke';
import { CrashEffects } from './components/CrashEffects';
import { AudioManager } from './audio/AudioManager';
import { formatCurrency } from './utils/format';
import { getGameInstance, isRealMode, gameApi } from './api';
import { gameStore } from './store/gameStore';
import { StartupOverlay } from './startup/StartupOverlay';
import { INIT_MAINTENANCE_GRACE_MS } from './startup/config';

class MemePumpApp {
	private balance: number = INITIAL_BALANCE;
	private game!: Game;
	private rocketAnimation!: RocketAnimation;
	private starsBackground!: StarsBackground | null;
	private floatingElements!: FloatingElements | null;
	private acidSmoke!: AcidSmoke | null;
	private crashEffects!: CrashEffects | null;
	private audio!: AudioManager;

	private phaseOverlayEl: HTMLElement | null = null;
	private phaseOverlayInlineHostEl: HTMLElement | null = null;
	private phaseCountEl: HTMLElement | null = null;
	private phaseLabelEl: HTMLElement | null = null;
	private phaseSubEl: HTMLElement | null = null;
	private phaseRingProgressEl: SVGCircleElement | null = null;
	private phaseRingContainerEl: HTMLElement | null = null;
	private overlayTimer: number | null = null;
	private betPhaseSeconds: number = BET_PHASE_MS / 1000;
	private initialBootstrapped: boolean = false;
	private balanceAnimRaf: number | null = null;
	private startupOverlay: StartupOverlay | null = null;
	private startupOverlayCompleted: boolean = false;

	// Real mode WS state
	private wsState: string = 'BET_TIME';
	private wsMultiplier: number = 1.0;
	private wsUnsubs: (() => void)[] = [];
	private reconnectToastTimer: number | null = null;
	private reconnectToastShown: boolean = false;
	private readonly reconnectToastTtlMs: number = 15000;
	private realSessionInitialized: boolean = false;
	private hasReceivedRoundState: boolean = false;
	private hasRenderableWsState: boolean = false;
	private suppressTransientFxUntil: number = 0;
	private lastCrashedRoundId: number | null = null;
	private maintenanceRedirectTimer: number | null = null;
	private maintenanceRedirectDeadlineAt: number | null = null;
	private maintenanceRedirected: boolean = false;
	private readonly initReconnectGraceMs: number = INIT_MAINTENANCE_GRACE_MS;
	private readonly runtimeReconnectGraceMs: number = 9000;

	constructor() {
		try {
			const stored = localStorage.getItem(STORAGE_KEY_BALANCE);
			if (stored) this.balance = Number(stored);
		} catch (e) {}
		this.initializeComponents();
		this.setupEventListeners();
		this.setupGameEvents();
		this.bootstrapInitialState();
	}

	private initializeComponents(): void {
		// In real mode, use a lightweight stub — no local game loop
		if (isRealMode) {
			this.game = {
				on() {}, off() {},
				getCurrentState() { return RoundState.BET_TIME; },
				getCurrentMultiplier() { return 1; },
				getCurrentRound() { return null; },
				getBets() { return []; },
				getRoundHistory() { return []; },
				getRoundNumber() { return 0; },
				isAutoBetEnabled() { return false; },
				pause() {}, resume() {}, destroy() {},
				setAutoBet() {},
				placeBet() { throw new Error('Use gameApi in real mode'); },
				cancelBet() { return false; },
				cashOut() { return false; },
				emit() {},
			} as unknown as Game;
		} else {
			// In mock mode, reuse the Game from MockGameApi
			const apiGame = getGameInstance();
			this.game = apiGame ?? new Game();
		}

		let audioPrefs; try { audioPrefs = JSON.parse(localStorage.getItem(STORAGE_KEY_AUDIO_PREFS)||'{}'); } catch(e) {}
		this.audio = new AudioManager({
			musicEnabled: audioPrefs?.musicEnabled !== false,
			effectsEnabled: audioPrefs?.effectsEnabled !== false
		});
		this.audio.startBackground();

		const rocketContainerElement = document.querySelector('.rocket-container') as HTMLElement | null;

		this.rocketAnimation = rocketContainerElement
			? new RocketAnimation(rocketContainerElement, this.game)
			: ({ destroy(){}, handleStateChange(){}, updateMultiplier(){}, clearCrashState(){} } as any);

		this.updateBalanceUI();

		this.phaseOverlayEl = document.getElementById('round-phase-overlay');
		this.phaseOverlayInlineHostEl = document.querySelector('.main-visual') as HTMLElement | null;
		this.phaseCountEl = document.getElementById('rpo-count');
		this.phaseLabelEl = document.getElementById('rpo-phase');
		this.phaseSubEl = document.getElementById('rpo-sub');
		this.phaseRingProgressEl = document.querySelector<SVGCircleElement>('#rpo-ring-progress');
		this.phaseRingContainerEl = document.getElementById('rpo-ring-wrapper');
		this.startupOverlay = new StartupOverlay({
			onRevealStart: () => {
				this.setBootPendingDom(false);
				this.setAppShellPending(false);
			},
			onComplete: () => {
				this.startupOverlayCompleted = true;
				this.setBootPendingDom(false);
				this.setAppShellPending(false);
				document.body.removeAttribute('aria-busy');
			}
		});

		try {
			const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
			this.starsBackground = new StarsBackground('stars-canvas');
			if (this.starsBackground && isMobile) {
				this.starsBackground.setPerformanceMode(true);
			}
		} catch (error) {
			console.warn('[MemePump] Failed to initialize stars background:', error);
			this.starsBackground = null;
		}

		try {
			this.floatingElements = new FloatingElements('floating-elements-container');
		} catch (error) {
			console.warn('[MemePump] Failed to initialize floating elements:', error);
			this.floatingElements = null;
		}

		try {
			this.acidSmoke = new AcidSmoke('acid-smoke-canvas', '.rocket-container');
		} catch (error) {
			console.warn('[MemePump] Failed to initialize acid smoke:', error);
			this.acidSmoke = null;
		}

		try {
			this.crashEffects = new CrashEffects('crash-effects');
		} catch (error) {
			console.warn('[MemePump] Failed to initialize crash effects:', error);
			this.crashEffects = null;
		}
	}

	private setupEventListeners(): void {
		window.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				if (!isRealMode) {
					try { this.game.pause(); } catch {}
				}
				this.stopCountdown();
				try { this.starsBackground?.stop(); } catch {}
				try { this.floatingElements?.stop(); } catch {}
				try { this.acidSmoke?.stop(); } catch {}
				return;
			}
			if (isRealMode) {
				// Suppress queued transient effects after tab resume; render from latest WS state.
				this.suppressTransientFxUntil = Date.now() + 1500;
			}
			if (!isRealMode) {
				try { this.game.resume(); } catch {}
			}
			try { this.starsBackground?.start(); } catch {}
			const currentState = isRealMode ? this.wsState : this.game.getCurrentState();
			if (currentState === RoundState.RUNNING || currentState === 'RUNNING') {
				try { this.floatingElements?.start(); } catch {}
				try { this.acidSmoke?.start(); } catch {}
			}
			this.syncOnVisibility();
		});
	}

	private syncOnVisibility(): void {
		if (isRealMode) {
			// In real mode, re-sync visuals from WS state
			if (this.wsState === 'RUNNING') {
				try { (this.rocketAnimation as any).clearCrashState?.(); } catch {}
				this.hidePhaseOverlay();
				try { (this.rocketAnimation as any).handleStateChange?.('ROUND_START'); } catch {}
				try { (this.rocketAnimation as any).handleStateChange?.('RUNNING'); } catch {}
				try { (this.rocketAnimation as any).updateMultiplier?.(this.wsMultiplier); } catch {}
				try { this.floatingElements?.start(); } catch {}
				try { this.acidSmoke?.start(); } catch {}
			} else if (this.wsState === 'BET_TIME') {
				try { (this.rocketAnimation as any).clearCrashState?.(); } catch {}
				try { (this.rocketAnimation as any).handleStateChange?.('BET_TIME'); } catch {}
			} else if (this.wsState === 'CRASH') {
				this.hidePhaseOverlay();
				// Don't clear crash state — let the crash X remain visible
			}
			return;
		}
		try { (this.rocketAnimation as any).clearCrashState(); } catch {}
		const state = this.game.getCurrentState();
		if (state === RoundState.BET_TIME) {
			this.stopCountdown();
			let remaining = this.betPhaseSeconds;
			const round = this.game.getCurrentRound();
			if (round) {
				const elapsed = (Date.now() - round.startTime) / 1000;
				remaining = Math.max(0, this.betPhaseSeconds - elapsed);
			}
			this.showPhaseOverlay('BET_TIME', remaining);
			this.startCountdown(remaining);
			try { (this.rocketAnimation as any).handleStateChange('BET_TIME'); } catch {}
			return;
		}
		if (state === RoundState.ROUND_START || state === RoundState.RUNNING) {
			this.stopCountdown();
			this.hidePhaseOverlay();
			try { (this.rocketAnimation as any).handleStateChange(state); } catch {}
			try {
				const mult = this.game.getCurrentMultiplier();
				(this.rocketAnimation as any).updateMultiplier(mult);
			} catch {}
			return;
		}
		if (state === RoundState.CRASH) {
			this.stopCountdown();
			this.hidePhaseOverlay();
		}
	}

	private toast(kind: 'success' | 'error' | 'info' | 'warning', text: string, ttl?: number): void {
		const fn = (window as any).__memePumpShowToast;
		if (typeof fn === 'function') fn(kind, text, ttl);
	}

	private deriveBetTimeRemaining(startsAt: number | null | undefined): number | null {
		if (!startsAt) return null;
		const remaining = Math.max(0, (startsAt - Date.now()) / 1000);
		if (remaining > 60) return this.betPhaseSeconds;
		return remaining;
	}

	private shouldPlayTransientRoundEffects(eventTimestamp?: number): boolean {
		if (document.hidden) return false;
		if (Date.now() < this.suppressTransientFxUntil) return false;
		if (typeof eventTimestamp === 'number') {
			const ageMs = Date.now() - eventTimestamp;
			if (ageMs > 4000) return false;
		}
		return true;
	}

	private setupGameEvents(): void {
		if (isRealMode) {
			this.setupRealModeEvents();
			return;
		}

		this.game.on('betActivated', (event) => {
			const amt = event.data.bet.amount;
			if (amt > 0) {
				this.debit(amt);
			}
		});

		this.game.on('betCanceled', () => {
			this.audio.play('ui-toggle', { allowRestart: true });
		});

        this.game.on('betLoss', () => {
            this.audio.play('loss', { allowRestart: true });
        });

		this.game.on('rocketLaunch', () => {
			this.audio.play('launch', { allowRestart: true });
		});

		this.game.on('crash', (event) => {
			try {
				this.crashEffects?.triggerCrash(event.data.multiplier);
			} catch (error) {
				console.warn('[MemePump] Failed to trigger crash effects:', error);
			}
			
			this.audio.play('crash', { allowRestart: true });
		});

		this.game.on('cashout', (event) => {
			const payout = event.data.winAmount;
			if (payout > 0) {
				this.credit(payout);
				this.audio.play('cashout', { allowRestart: true });
			}
		});

		this.game.on('milestone', () => {
			this.audio.play('ui-toggle', { allowRestart: true });
		});

		this.game.on('stateChange', (event) => {
			const st = event.data?.state as RoundState | undefined;
			if (st === RoundState.BET_TIME) {
				this.stopCountdown();
				let remaining = this.betPhaseSeconds;
				const round = this.game.getCurrentRound();
				if (round) {
					const elapsed = (Date.now() - round.startTime) / 1000;
					remaining = Math.max(0, this.betPhaseSeconds - elapsed);
			}
			this.showPhaseOverlay('BET_TIME', remaining);
			this.startCountdown(remaining);
			try { this.floatingElements?.stop(); this.floatingElements?.clear(); } catch {}
			try { this.crashEffects?.clear(); } catch {}
		}
			else if (st === RoundState.ROUND_START) {
				this.stopCountdown();
				this.hidePhaseOverlay();
			}
			else if (st === RoundState.RUNNING) {
				this.stopCountdown();
				this.hidePhaseOverlay();
				try { this.floatingElements?.start(); } catch {}
				try { this.acidSmoke?.start(); } catch {}
			}
			else if (st === RoundState.CRASH) {
				this.stopCountdown();
				this.hidePhaseOverlay();
				try { this.floatingElements?.stop(); } catch {}
				try { this.acidSmoke?.clear(); } catch {}
				setTimeout(() => {
					try { this.crashEffects?.clear(); } catch {}
				}, 3000);
			}
		});
		if (this.game.getCurrentState() === RoundState.BET_TIME) {
			const round = this.game.getCurrentRound();
			let remaining = this.betPhaseSeconds;
			if (round) {
				const elapsed = (Date.now() - round.startTime) / 1000;
				remaining = Math.max(0, this.betPhaseSeconds - elapsed);
			}
			this.showPhaseOverlay('BET_TIME', remaining);
			this.startCountdown(remaining);
		}
	}

	private setupRealModeEvents(): void {
		this.wsUnsubs.push(gameApi.on('connection:status', (payload) => {
			const status = payload.status ?? (payload.connected ? 'open' : 'closed');

			if (payload.connected) {
				this.clearMaintenanceRedirect();
				if (this.reconnectToastTimer) {
					window.clearTimeout(this.reconnectToastTimer);
					this.reconnectToastTimer = null;
				}
				if (this.reconnectToastShown) {
					this.toast('success', 'Connection restored');
					this.reconnectToastShown = false;
				}
				return;
			}

			if (status !== 'closed' && status !== 'reconnecting') {
				return;
			}

			if (!this.realSessionInitialized) {
				this.scheduleMaintenanceRedirect(this.initReconnectGraceMs, 'connection_lost');
				return;
			}

			this.scheduleMaintenanceRedirect(this.runtimeReconnectGraceMs, 'connection_lost_runtime', true);

			if (this.reconnectToastShown) return;
			if (this.reconnectToastTimer) return;
			this.reconnectToastTimer = window.setTimeout(() => {
				this.reconnectToastTimer = null;
				this.reconnectToastShown = true;
				const remainingToRedirectMs = this.maintenanceRedirectDeadlineAt
					? Math.max(500, this.maintenanceRedirectDeadlineAt - Date.now())
					: this.reconnectToastTtlMs;
				this.toast('warning', 'Connection lost. Reconnecting...', remainingToRedirectMs);
			}, 300);
		}));

		// round:state → overlay, rocket, visual effects
		this.wsUnsubs.push(gameApi.on('round:state', (payload) => {
			this.hasReceivedRoundState = true;
			this.wsState = payload.state;
			if (payload.state === 'BET_TIME') {
				this.stopCountdown();
				const remaining = this.deriveBetTimeRemaining(payload.startsAt);
				if (remaining !== null && remaining > 0.2) {
					this.showPhaseOverlay('BET_TIME', remaining);
					this.startCountdown(remaining);
					this.hasRenderableWsState = true;
				} else {
					this.hidePhaseOverlay();
					this.hasRenderableWsState = false;
				}
				this.tryMarkStartupReady();
				try { this.floatingElements?.stop(); this.floatingElements?.clear(); } catch {}
				try { this.crashEffects?.clear(); } catch {}
				try { (this.rocketAnimation as any).clearCrashState?.(); } catch {}
				try { (this.rocketAnimation as any).handleStateChange?.('BET_TIME'); } catch {}
			} else if (payload.state === 'RUNNING') {
				this.stopCountdown();
				this.hidePhaseOverlay();
				this.wsMultiplier = payload.multiplier > 0 ? payload.multiplier : 1;
				// WS has no ROUND_START — synthesise it so RocketAnimation reveals the rocket
				try { (this.rocketAnimation as any).handleStateChange?.('ROUND_START'); } catch {}
				try { (this.rocketAnimation as any).handleStateChange?.('RUNNING'); } catch {}
				try { (this.rocketAnimation as any).updateMultiplier?.(this.wsMultiplier); } catch {}
				try { this.floatingElements?.start(); } catch {}
				try { this.acidSmoke?.start(); } catch {}
				this.hasRenderableWsState = true;
				this.tryMarkStartupReady();
				if (this.shouldPlayTransientRoundEffects()) {
					this.audio.play('launch', { allowRestart: true });
				}
			} else if (payload.state === 'CRASH') {
				this.stopCountdown();
				this.hidePhaseOverlay();
				try { (this.rocketAnimation as any).crashRocket?.(payload.crashPoint ?? payload.multiplier); } catch {}
				try { this.floatingElements?.stop(); } catch {}
				try { this.acidSmoke?.clear(); } catch {}
				this.hasRenderableWsState = true;
				this.tryMarkStartupReady();
				setTimeout(() => { try { this.crashEffects?.clear(); } catch {} }, 3000);
			}
		}));

		// tick → rocket multiplier
		this.wsUnsubs.push(gameApi.on('tick', (payload) => {
			this.wsMultiplier = payload.multiplier;
			if (this.wsState !== 'RUNNING') {
				this.wsState = 'RUNNING';
				this.stopCountdown();
				this.hidePhaseOverlay();
				try { (this.rocketAnimation as any).handleStateChange?.('ROUND_START'); } catch {}
				try { (this.rocketAnimation as any).handleStateChange?.('RUNNING'); } catch {}
				try { this.floatingElements?.start(); } catch {}
				try { this.acidSmoke?.start(); } catch {}
			}
			this.hasRenderableWsState = true;
			this.tryMarkStartupReady();
			try { (this.rocketAnimation as any).updateMultiplier?.(payload.multiplier); } catch {}
		}));

		// round:crashed → rocket crash + crash effects + audio
		this.wsUnsubs.push(gameApi.on('round:crashed', (payload) => {
			if (this.lastCrashedRoundId === payload.roundId) return;
			this.lastCrashedRoundId = payload.roundId;
			try { (this.rocketAnimation as any).crashRocket?.(payload.crashPoint); } catch {}
			if (this.shouldPlayTransientRoundEffects(payload.timestamp)) {
				try { this.crashEffects?.triggerCrash(payload.crashPoint); } catch {}
				this.audio.play('crash', { allowRestart: true });
			}
		}));

		// session:init → balance + initial visual state
		this.wsUnsubs.push(gameApi.on('session:init', (payload) => {
			this.realSessionInitialized = true;
			this.clearMaintenanceRedirect();
			this.balance = payload.balance.amount;
			this.updateBalanceUI();

			if (payload.roundSnapshot) {
				this.wsState = payload.roundSnapshot.state;
				if (payload.roundSnapshot.state === 'BET_TIME') {
					const remaining = this.deriveBetTimeRemaining(payload.roundSnapshot.startsAt);
					if (remaining !== null && remaining > 0.2) {
						this.showPhaseOverlay('BET_TIME', remaining);
						this.startCountdown(remaining);
						this.hasRenderableWsState = true;
					} else {
						this.hidePhaseOverlay();
						this.hasRenderableWsState = false;
					}
					try { (this.rocketAnimation as any).handleStateChange?.('BET_TIME'); } catch {}
				} else if (payload.roundSnapshot.state === 'RUNNING') {
					this.hidePhaseOverlay();
					this.wsMultiplier = payload.roundSnapshot.multiplier > 0 ? payload.roundSnapshot.multiplier : 1;
					// WS has no ROUND_START — synthesise it so RocketAnimation reveals the rocket
					try { (this.rocketAnimation as any).handleStateChange?.('ROUND_START'); } catch {}
					try { (this.rocketAnimation as any).handleStateChange?.('RUNNING'); } catch {}
					try { (this.rocketAnimation as any).updateMultiplier?.(this.wsMultiplier); } catch {}
					try { this.floatingElements?.start(); } catch {}
					try { this.acidSmoke?.start(); } catch {}
					this.hasRenderableWsState = true;
				} else if (payload.roundSnapshot.state === 'CRASH') {
					this.hidePhaseOverlay();
					try { (this.rocketAnimation as any).crashRocket?.(payload.roundSnapshot.crashPoint ?? payload.roundSnapshot.multiplier); } catch {}
					this.hasRenderableWsState = true;
				}
			}
			this.tryMarkStartupReady();
		}));

		// balance:update → balance display + animation
		this.wsUnsubs.push(gameApi.on('balance:update', (payload) => {
			this.balance = payload.amount;
			this.updateBalanceUI();
			if (payload.delta > 0) {
				this.animateBalanceWin(payload.delta);
			}
		}));

		// bet:place ack → update balance
		this.wsUnsubs.push(gameApi.on('bet:place', (payload) => {
			this.balance = payload.newBalance;
			this.updateBalanceUI();
		}));

		// bet:cancel ack → update balance + audio
		this.wsUnsubs.push(gameApi.on('bet:cancel', (payload) => {
			this.balance = payload.newBalance;
			this.updateBalanceUI();
			this.audio.play('ui-toggle', { allowRestart: true });
		}));

		// bet:cashout ack → balance + animation + audio
		this.wsUnsubs.push(gameApi.on('bet:cashout', (payload) => {
			const delta = payload.newBalance - this.balance;
			this.balance = payload.newBalance;
			this.updateBalanceUI();
			if (delta > 0) {
				this.animateBalanceWin(delta);
			}
			this.audio.play('cashout', { allowRestart: true });
		}));

		// auto:cashout → audio
		this.wsUnsubs.push(gameApi.on('auto:cashout', () => {
			this.audio.play('cashout', { allowRestart: true });
		}));

		this.wsUnsubs.push(gameApi.on('error', () => {
			if (!this.realSessionInitialized) {
				this.scheduleMaintenanceRedirect(this.initReconnectGraceMs, 'init_error');
			}
		}));

		// If session data already arrived before we subscribed (race)
		if (gameStore.balanceLoaded()) {
			this.balance = gameStore.balance();
			this.updateBalanceUI();
		}
	}

	private updateBalanceUI(): void {
		const el = document.getElementById('balance-amount');
		if (!el) return;
		el.textContent = formatCurrency(this.balance);
		try { localStorage.setItem(STORAGE_KEY_BALANCE, String(this.balance)); } catch (e) {}
	}

	private animateBalanceWin(delta: number): void {
		try {
			if (!(delta > 0)) return;
			const header = document.getElementById('header-balance');
			const label = document.getElementById('balance-amount');
			if (!header || !label) return;

			if (this.balanceAnimRaf) { cancelAnimationFrame(this.balanceAnimRaf); this.balanceAnimRaf = null; }

			const startValue = Math.max(0, (this.balance - delta));
			const endValue = this.balance;
			const dur = 800;
			const fmt = (v: number) => formatCurrency(Number(v.toFixed(2)));
			const finalText = fmt(endValue);

			const measureTextWidth = (text: string) => {
				const probe = document.createElement('span');
				probe.className = (label.className || '') + ' balance-amount-probe';
				probe.style.position = 'absolute';
				probe.style.visibility = 'hidden';
				probe.style.whiteSpace = 'nowrap';
				probe.textContent = text;
				document.body.appendChild(probe);
				const w = probe.getBoundingClientRect().width;
				try { document.body.removeChild(probe); } catch {}
				return w;
			};
			const currentWidth = header.getBoundingClientRect().width;
			let finalWidth = currentWidth;
			try { finalWidth = Math.max(currentWidth, measureTextWidth(finalText) + 24); } catch {}
			header.style.width = Math.ceil(finalWidth) + 'px';

			header.classList.remove('balance-bump');
			void (header as any).offsetWidth;
			header.classList.add('balance-bump');

			const t0 = performance.now();
			const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
			const step = () => {
				const t = (performance.now() - t0) / dur;
				if (t >= 1) {
					label.textContent = finalText;
					this.balanceAnimRaf = null;
					setTimeout(() => { try { header.style.removeProperty('width'); } catch {} }, 60);
					return;
				}
				const p = easeOutCubic(Math.max(0, Math.min(1, t)));
				const cur = startValue + (endValue - startValue) * p;
				label.textContent = fmt(cur);
				this.balanceAnimRaf = requestAnimationFrame(step);
			};
			this.balanceAnimRaf = requestAnimationFrame(step);

			const rect = label.getBoundingClientRect();
			const fly = document.createElement('div');
			fly.className = 'balance-flyout anim';
			const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
			fly.textContent = `+${nf.format(delta)}`;
			fly.style.left = `${rect.left + rect.width - 8}px`;
			fly.style.top = `${rect.top + rect.height/2}px`;
			document.body.appendChild(fly);
			setTimeout(() => { try { document.body.removeChild(fly); } catch {} }, 1400);

			const count = 8;
			for (let i=0;i<count;i++) {
				const sp = document.createElement('div');
				sp.className = 'balance-sparkle anim';
				const angle = (Math.PI * 2) * (i / count) + (Math.random()*0.4-0.2);
				const dist = 40 + Math.random()*30;
				const tx = Math.cos(angle) * dist;
				const ty = Math.sin(angle) * -dist;
				sp.style.left = `${rect.left + rect.width - 12}px`;
				sp.style.top = `${rect.top + rect.height/2 - 6}px`;
				sp.style.setProperty('--sparkle-translate', `translate(${tx}px, ${ty}px)`);
				sp.style.setProperty('--sparkle-rot', `${(Math.random()*180+90).toFixed(0)}deg`);
				sp.style.setProperty('--sparkle-dur', `${(700+Math.random()*500).toFixed(0)}ms`);
				document.body.appendChild(sp);
				setTimeout(() => { try { document.body.removeChild(sp); } catch {} }, 1200);
			}
		} catch {}
	}

	private credit(amount: number): void {
		const prev = this.balance;
		const next = Number((this.balance + amount).toFixed(2));
		this.balance = next;
		this.updateBalanceUI();
		if (next > prev) this.animateBalanceWin(next - prev);
	}

	private debit(amount: number): void {
		this.balance = Number((this.balance - amount).toFixed(2));
		if (this.balance < 0) this.balance = 0;
		this.updateBalanceUI();
	}

	private getStoredAudioPref(key: string, defaultValue: boolean): boolean {
		try {
			const stored = localStorage.getItem(STORAGE_KEY_AUDIO_PREFS);
			if (stored) {
				const prefs = JSON.parse(stored);
				return prefs[key] !== undefined ? prefs[key] : defaultValue;
			}
		} catch (e) {}
		return defaultValue;
	}

	private persistAudioPrefs(prefs: { musicEnabled?: boolean, effectsEnabled?: boolean }): void {
		try {
			const existing = localStorage.getItem(STORAGE_KEY_AUDIO_PREFS);
			const current = existing ? JSON.parse(existing) : {};
			const updated = { ...current, ...prefs };
			localStorage.setItem(STORAGE_KEY_AUDIO_PREFS, JSON.stringify(updated));
		} catch (e) {}
	}

	getGame(): Game {
		return this.game;
	}

	destroy(): void {
		this.wsUnsubs.forEach(u => u());
		this.wsUnsubs = [];
		this.clearMaintenanceRedirect();
		if (this.reconnectToastTimer) {
			window.clearTimeout(this.reconnectToastTimer);
			this.reconnectToastTimer = null;
		}
		this.game.destroy();
		this.rocketAnimation.destroy();
		this.stopCountdown();
	}

	private scheduleMaintenanceRedirect(delayMs: number, reason: string, allowAfterSessionInit: boolean = false): void {
		if (!isRealMode || this.maintenanceRedirected) return;
		if (!allowAfterSessionInit && this.realSessionInitialized) return;
		if (this.maintenanceRedirectTimer) return;
		this.maintenanceRedirectDeadlineAt = Date.now() + delayMs;
		this.maintenanceRedirectTimer = window.setTimeout(() => {
			this.maintenanceRedirectTimer = null;
			this.maintenanceRedirectDeadlineAt = null;
			if ((!allowAfterSessionInit && this.realSessionInitialized) || this.maintenanceRedirected) return;
			this.redirectToMaintenance(reason);
		}, delayMs);
	}

	private clearMaintenanceRedirect(): void {
		if (!this.maintenanceRedirectTimer) return;
		window.clearTimeout(this.maintenanceRedirectTimer);
		this.maintenanceRedirectTimer = null;
		this.maintenanceRedirectDeadlineAt = null;
	}

	private redirectToMaintenance(reason: string): void {
		if (this.maintenanceRedirected) return;
		this.maintenanceRedirected = true;
		const returnTo = `${location.pathname}${location.search}${location.hash}`;
		const url = new URL('/maintenance.html', location.origin);
		url.searchParams.set('returnTo', returnTo || '/');
		url.searchParams.set('reason', reason);
		location.replace(url.toString());
	}

	private restorePhaseOverlayToInlineHost(): void {
		if (!this.phaseOverlayEl || !this.phaseOverlayInlineHostEl) return;
		if (this.phaseOverlayEl.parentElement === this.phaseOverlayInlineHostEl) return;
		this.phaseOverlayInlineHostEl.appendChild(this.phaseOverlayEl);
	}

	private setMainVisualOverlayActive(active: boolean): void {
		if (!this.phaseOverlayInlineHostEl) return;
		this.phaseOverlayInlineHostEl.classList.toggle('overlay-active', active);
	}

	private setAppShellPending(active: boolean): void {
		const root = document.documentElement;
		if (active) {
			root.setAttribute('data-app-shell', 'pending');
			return;
		}
		root.removeAttribute('data-app-shell');
	}

	private setBootPendingDom(active: boolean): void {
		const root = document.documentElement;
		if (active && isRealMode) {
			root.setAttribute('data-app-boot', 'pending');
			return;
		}
		root.removeAttribute('data-app-boot');
	}

	private startStartupOverlay(): void {
		this.startupOverlayCompleted = false;
		this.setAppShellPending(true);
		this.setBootPendingDom(true);
		document.body.setAttribute('aria-busy', 'true');
		this.startupOverlay?.start();
	}

	private tryMarkStartupReady(): void {
		if (this.startupOverlayCompleted) return;
		if (isRealMode) {
			if (!this.realSessionInitialized || !this.hasRenderableWsState) {
				return;
			}
		}
		this.startupOverlay?.markBackendReady();
	}

	private showPhaseOverlay(kind: 'BET_TIME' | 'CRASH' | 'ROUND_START', remaining?: number) {
		if (!this.phaseOverlayEl) return;
		if (kind !== 'BET_TIME') return;
		this.restorePhaseOverlayToInlineHost();
		this.setMainVisualOverlayActive(true);
		this.phaseOverlayEl.classList.remove('pre-init-hidden');
		this.phaseOverlayEl.classList.add('show');
		this.phaseOverlayEl.dataset.variant = 'phase';
		this.phaseOverlayEl.dataset.screen = 'inline';
		this.phaseOverlayEl.setAttribute('aria-hidden', 'false');
		document.body.removeAttribute('aria-busy');
		if (this.phaseLabelEl) this.phaseLabelEl.textContent = 'Place Bets';
		if (this.phaseSubEl) this.phaseSubEl.textContent = 'Next launch';
		if (this.phaseCountEl) {
			if (typeof remaining === 'number') {
				this.phaseCountEl.textContent = Math.max(0, Math.ceil(remaining)).toString();
			} else {
				this.phaseCountEl.textContent = '--';
			}
		}
		if (this.phaseRingProgressEl) this.phaseRingProgressEl.dataset.mode = 'BET_TIME';
		this.resetRing();
	}
	private hidePhaseOverlay() {
		if (!this.phaseOverlayEl) return;
		this.phaseOverlayEl.classList.remove('pre-init-hidden');
		this.phaseOverlayEl.classList.remove('show');
		this.phaseOverlayEl.dataset.variant = 'phase';
		this.phaseOverlayEl.dataset.screen = 'inline';
		this.phaseOverlayEl.setAttribute('aria-hidden', 'true');
		this.restorePhaseOverlayToInlineHost();
		this.setMainVisualOverlayActive(false);
		document.body.removeAttribute('aria-busy');
	}


	private startCountdown(totalSeconds: number) {
		if (this.overlayTimer) { window.clearInterval(this.overlayTimer); this.overlayTimer = null; }
		const startMs = performance.now();
		this.updateRing(totalSeconds, totalSeconds, 'BET_TIME', true);
		const tick = () => {
			const elapsed = (performance.now() - startMs) / 1000;
			const remaining = totalSeconds - elapsed;
			if (this.phaseCountEl) this.phaseCountEl.textContent = Math.max(0, Math.ceil(remaining)).toString();
			this.updateRing(totalSeconds, Math.max(0, remaining), 'BET_TIME');
			if (remaining <= 0) {
				this.stopCountdown();
			}
		};
		this.overlayTimer = window.setInterval(tick, 200);
		if (this.phaseCountEl) this.phaseCountEl.textContent = Math.ceil(totalSeconds).toString();
	}
	private stopCountdown() {
		if (this.overlayTimer) { window.clearInterval(this.overlayTimer); this.overlayTimer = null; }
		if (this.phaseRingContainerEl) this.phaseRingContainerEl.classList.remove('finishing');
	}

	private resetRing() {
		if (!this.phaseRingProgressEl) return;
		const circumference = this.getRingCircumference();
		this.phaseRingProgressEl.style.strokeDasharray = circumference.toString();
		this.phaseRingProgressEl.style.strokeDashoffset = '0';
		if (this.phaseRingContainerEl) {
			this.phaseRingContainerEl.classList.remove('finishing');
			this.phaseRingContainerEl.classList.remove('syncing');
		}
	}
	private getRingCircumference(): number {
		if (!this.phaseRingProgressEl) return 289.026;
		const rAttr = this.phaseRingProgressEl.getAttribute('r');
		const r = rAttr ? parseFloat(rAttr) : 46;
		return 2 * Math.PI * r;
	}
	private updateRing(total: number, remaining: number, mode: string, force?: boolean) {
		if (!this.phaseRingProgressEl) return;
		if (total <= 0) return;
		const circumference = this.getRingCircumference();
		const elapsed = total - remaining;
		const ratio = Math.min(1, Math.max(0, elapsed / total));
		const dashOffset = -(circumference * ratio);
		if (force || Math.abs(parseFloat(this.phaseRingProgressEl.style.strokeDashoffset || '0') - dashOffset) > 0.5) {
			this.phaseRingProgressEl.style.strokeDashoffset = dashOffset.toFixed(2);
		}
		if (remaining <= 1.05 && remaining > 0) {
			if (this.phaseRingContainerEl && !this.phaseRingContainerEl.classList.contains('finishing')) {
				this.phaseRingContainerEl.classList.add('finishing');
			}
		} else if (remaining > 1.2) {
			if (this.phaseRingContainerEl) this.phaseRingContainerEl.classList.remove('finishing');
		}
	}

	private bootstrapInitialState(): void {
		if (this.initialBootstrapped) return;
		this.startStartupOverlay();
		// In real mode, initial state comes from session:init event
		if (isRealMode) {
			this.scheduleMaintenanceRedirect(this.initReconnectGraceMs, 'init_timeout');
			this.initialBootstrapped = true;
			return;
		}
		const state = this.game.getCurrentState();
		if (state === RoundState.BET_TIME) {
			const round = this.game.getCurrentRound();
			let remaining = this.betPhaseSeconds;
			if (round) {
				const elapsed = (Date.now() - round.startTime) / 1000;
				remaining = Math.max(0, this.betPhaseSeconds - elapsed);
			}
			this.showPhaseOverlay('BET_TIME', remaining);
			this.startCountdown(remaining);
			try { (this.rocketAnimation as any).handleStateChange('BET_TIME'); } catch(e) {}
		}
		this.tryMarkStartupReady();
		this.initialBootstrapped = true;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	try {
		const app = new MemePumpApp();
		(window as any).memePumpApp = app;
		(window as any).openModal = (id: string) => {
			console.debug('[openModal] stub – SolidJS ModalManager handles modals', id);
			return false;
		};
		(window as any).memePumpToggleMusic = () => {
			try {
				const current = (app as any).getStoredAudioPref?.('musicEnabled', true) ?? true;
				const next = !current;
				(app as any).persistAudioPrefs?.({ musicEnabled: next });
				(app as any).audio?.setPreferences({ musicEnabled: next });
				if (next) { (app as any).audio?.startBackground(); (app as any).audio?.play('ui-toggle',{allowRestart:true}); }
				else { (app as any).audio?.stopBackground(); }
				return next;
			} catch(e) { return null; }
		};
		(window as any).memePumpToggleSfx = () => {
			try {
				const current = (app as any).getStoredAudioPref?.('effectsEnabled', true) ?? true;
				const next = !current;
				(app as any).persistAudioPrefs?.({ effectsEnabled: next });
				(app as any).audio?.setPreferences({ effectsEnabled: next });
				(app as any).audio?.play('ui-toggle', { allowRestart: true });
				return next;
			} catch(e) { return null; }
		};
		try { window.dispatchEvent(new CustomEvent('memePump:ready')); } catch {}
		console.log('🚀 Meme Pump initialized');
	} catch (error) {
		document.documentElement.removeAttribute('data-app-shell');
		document.documentElement.removeAttribute('data-app-boot');
		console.error('Failed to initialize Meme Pump:', error);
	}
});
